import React, { ComponentType, forwardRef, HTMLAttributeAnchorTarget, ReactNode, useCallback } from 'react';
import { Loggable } from '@direct-frontend/modules-logger';
import { PressProps } from 'react-aria';
import { genericMemo } from '@direct-frontend/stdlib';

import { TestProps } from '../../internal/types/testTypes';
import { ColorlessIconProps } from '../../icons';
import { useTheme } from '../../lib/theme/ThemeProvider';
import { cn } from '../../lib/css/className/className';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { VStack } from '../Stack';
import { ButtonBase } from '../ButtonBase';
import { Spinner } from '../Spinner/Spinner';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { SkinColor, SkinRole } from '../../lib/skin/getColorSkin/generated/types';

import { FloatingButtonColor, FloatingButtonSize } from './types';

import './FloatingButton.css';

const cls = cn('FloatingButton');

type FloatingButtonContentProps =
    | {
          /**
           * Используемая иконка, цвет и размер определяются автоматически
           */
          icon: ComponentType<ColorlessIconProps>;
          /**
           * Признак состояния загрузки (троббер на кнопке)
           */
          isLoading?: false;
      }
    | {
          /**
           * Признак состояния загрузки (троббер на кнопке)
           */
          isLoading: true;
          /**
           * Используемая иконка, цвет и размер определяются автоматически
           */
          icon?: ComponentType<ColorlessIconProps>;
      };

export type FloatingButtonProps = PressProps &
    TestProps &
    Loggable &
    FloatingButtonContentProps & {
        /**
         * Дополнительный класс кнопки
         */
        className?: string;

        /**
         * Цветовое оформление кнопки
         */
        color: FloatingButtonColor;

        /**
         * Размер кнопки
         */
        size: FloatingButtonSize;

        /**
         * Обработчик нажатия на кнопку
         */
        onClick?: () => void;

        /**
         * Текст во всплывающей подсказке при наведении
         *
         * @notInFigma
         */
        title?: string;

        /**
         * HTML-атрибут href для кнопки, при его передаче кнопка становится ссылкой
         */
        href?: string;

        /**
         * HTML-атрибут target для ссылки
         */
        target?: HTMLAttributeAnchorTarget;

        /**
         * HTML-атрибут rel для ссылки
         */
        rel?: string;
    };

const skinMap: Record<FloatingButtonColor, SkinColor | SkinRole> = {
    accent: 'accent',
    contrast: 'gray-neutral',
};

/**
 * Компонент для создания Парящей кнопки (Floating button, FAB)
 */
export const FloatingButton = genericMemo(
    forwardRef<HTMLButtonElement, FloatingButtonProps>(function FloatingButton(props, forwardedRef) {
        const {
            icon: Icon,
            color,
            size,
            isLoading,
            className,
            isDisabled,
            'data-testid': dataTestId = 'FloatingButton',
        } = props;

        const { iconSize } = useTheme().sizeTokens[size];
        const sizeSkinClass = getSizeSkin(size);

        const getClassName = useCallback(
            ({ isHovered, isPressed }: { isHovered?: boolean; isPressed?: boolean }) => {
                const skinClass = getColorSkin(skinMap[color], {
                    isHovered: isHovered || isPressed,
                    isDisabled,
                });

                return cls({}, [className, skinClass, sizeSkinClass]);
            },
            [className, color, sizeSkinClass, isDisabled],
        );

        const wrapIcon = useCallback((iconNode: ReactNode) => {
            return (
                <VStack justifyContent={'center'} alignItems={'center'} className={cls('icon')}>
                    {iconNode}
                </VStack>
            );
        }, []);

        let content;

        if (isLoading) {
            content = wrapIcon(<Spinner size={iconSize} color={'current'} />);
        } else {
            content = wrapIcon(<Icon size={iconSize} />);
        }

        return (
            <ButtonBase
                {...props}
                content={content}
                getClassName={getClassName}
                ref={forwardedRef}
                data-testid={dataTestId}
            />
        );
    }),
);
